import { Component, type ErrorInfo, type ReactNode } from "react"

type AppErrorBoundaryProps = {
  children: ReactNode
}

type AppErrorBoundaryState = {
  hasError: boolean
}

const isDomMutationError = (error: unknown) => {
  if (!(error instanceof Error)) return false

  return (
    error.name === "NotFoundError" &&
    (
      error.message.includes("removeChild") ||
      error.message.includes("insertBefore") ||
      error.message.includes("not a child of this node")
    )
  )
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    hasError: false,
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    console.error("Erro capturado pela aplicação:", error, errorInfo)

    if (isDomMutationError(error)) {
      document.documentElement.setAttribute("translate", "no")
      document.documentElement.classList.add("notranslate")
      document.body.setAttribute("translate", "no")
      document.body.classList.add("notranslate")
      document.getElementById("root")?.setAttribute("translate", "no")
      document.getElementById("root")?.classList.add("notranslate")
    }
  }

  private reloadPage = () => {
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-4 py-10 text-foreground">
        <section className="w-full max-w-md rounded-lg border bg-card p-6 shadow-sm">
          <h1 className="text-xl font-semibold">Não foi possível carregar esta tela</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Atualize a página para continuar. O WeAuto bloqueia traduções automáticas nesta área para evitar conflitos com o navegador.
          </p>
          <button
            type="button"
            onClick={this.reloadPage}
            className="mt-6 inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Atualizar página
          </button>
        </section>
      </main>
    )
  }
}
